import { useMemo, useState } from 'react'
import { Upload } from 'lucide-react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../core/firebase'
import { useAuth } from '../../core/auth'
import { useDirectory } from '../admin/model'
import { useNorseTermTeachers } from './model'
import { parseJmc, matchTeachers, type JmcParse } from './jmc'
import { ErrorState } from '../../ui/States'

/**
 * Upload the student roster from a JMC Office export, as it comes.
 *
 * Read in the browser (jmc.ts), the seminar teachers matched to staff and
 * shown for the office to confirm, then sent to the importRoster function:
 * first as a preview (nothing written), then for real. The function applies
 * the same checks as the Sheet sync and refuses to turn off half the roster
 * by mistake. Admin only (norseterm.run); the function checks too.
 *
 * Preview mode ("View as…") never calls it: the write guard covers Firestore,
 * not functions, so the button is simply off.
 */

interface Result {
  summary: string; refused: string | null; dryRun: boolean
  added: number; updated: number; deactivated: number; unchanged: number; noSeminar: number
  skipped: Array<{ row: number; email: string; reason: string }>
}

export function RosterUpload() {
  const preview = !!useAuth().preview
  const { rows: directory } = useDirectory()
  const teachers = useNorseTermTeachers(true)
  const [file, setFile] = useState<{ name: string; text: string } | null>(null)
  const [course, setCourse] = useState('SEMINAR')
  const [chosen, setChosen] = useState<Record<string, string>>({})
  const [result, setResult] = useState<Result | null>(null)
  const [large, setLarge] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const parsed: JmcParse | null = useMemo(() => (file ? parseJmc(file.text, course) : null), [file, course])
  const staff = useMemo(() => {
    const teach = new Set(teachers.map((t) => t.email))
    return (directory ?? []).filter((d) => d.active !== false)
      .map((d) => ({ email: d.email, name: d.displayName, teaches: teach.has(d.email) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [directory, teachers])
  const guesses = useMemo(() => (parsed ? matchTeachers(parsed.teacherLabels, staff) : {}), [parsed, staff])
  const pick = (label: string) => chosen[label] ?? guesses[label] ?? ''
  const unmatched = (parsed?.teacherLabels ?? []).filter((l) => !pick(l))

  const read = async (f: File) => {
    setErr(null); setResult(null); setChosen({}); setLarge(false)
    setFile({ name: f.name, text: await f.text() })
  }

  const send = async (dryRun: boolean) => {
    if (!parsed) return
    setBusy(true); setErr(null)
    try {
      const rows = parsed.students.map((s) => ({
        email: s.email, first: s.first, last: s.last, student_id: s.studentId, grade: s.grade,
        seminar_teacher_email: s.teacherLabel ? pick(s.teacherLabel) : '', active: '',
      }))
      const call = httpsCallable<unknown, Result>(functions, 'importRoster')
      const res = await call({ rows, dryRun, allowLargeChange: large })
      setResult(res.data)
      if (!dryRun && !res.data.refused) setFile(null)
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const perLabel = (l: string) => parsed?.students.filter((s) => s.teacherLabel === l).length ?? 0
  const longNames = (parsed?.students ?? []).filter((s) => s.last.includes(' '))

  return (
    <section className="card" style={{ marginTop: 'var(--s-2)' }}>
      <h2 className="ov__h">Upload the student list from JMC</h2>
      <p className="ov__p">
        In jmc Office, open <strong>Attendance → Student → Lists</strong>, run your saved Norse Term list (name, grade,
        email, and Course Sections → Course Name and Teacher), then <strong>Export Data</strong>. Upload that file here
        as it is. You will see what would change before anything does.
      </p>
      {preview && <p className="opt__hint">Uploading is off while previewing as someone else.</p>}

      {!file || !result?.dryRun ? (
        <label className="plan__add" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <Upload size={16} aria-hidden="true" />
          {file ? `Chosen: ${file.name}. Choose a different file` : 'Choose the JMC export (.csv)'}
          <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} disabled={preview}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f) }} />
        </label>
      ) : null}

      {err && <ErrorState body={err} />}
      {parsed?.error && <ErrorState body={parsed.error} />}
      {result && !result.dryRun && !file && (
        <div className="notice notice--info" role="status" style={{ marginTop: 'var(--s-3)' }}>
          <span aria-hidden="true">✓</span>
          <div>
            <strong>Student list updated.</strong> {result.added} added, {result.updated} updated, {result.deactivated} turned off,{' '}
            {result.unchanged} unchanged.{result.noSeminar > 0 && <> {result.noSeminar} have no seminar; set one by clicking their name below if they need one.</>}
          </div>
        </div>
      )}

      {parsed && !parsed.error && (
        <>
          <p className="opt__hint">
            {parsed.students.length} students found · {parsed.students.filter((s) => s.teacherLabel).length} in a seminar
            {parsed.noEmail ? ` · ${parsed.noEmail} rows with no email left out` : ''}
          </p>
          <label style={{ display: 'block', fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink-2)' }}>
            The seminar class is called
            <input className="opt__input" style={{ marginTop: 4, maxWidth: 260 }} value={course} onChange={(e) => { setCourse(e.target.value); setResult(null) }} />
          </label>

          <h3 className="opt__label" style={{ marginTop: 'var(--s-4)' }}>Check the seminar teachers</h3>
          <p className="opt__hint" style={{ marginTop: 0 }}>
            JMC writes teachers like &ldquo;Mr. Boman&rdquo;. These are matched to SailNorse staff; change any that are wrong.
          </p>
          <ul className="work">
            {parsed.teacherLabels.map((l) => (
              <li key={l} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{l}</div>
                  <div className="work__sub">{perLabel(l)} students</div>
                </div>
                <select className="opt__input" style={{ maxWidth: 260 }} value={pick(l)} aria-label={`SailNorse staff for ${l}`}
                  onChange={(e) => { setChosen((c) => ({ ...c, [l]: e.target.value })); setResult(null) }}>
                  <option value="">Choose…</option>
                  {staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}
                </select>
              </li>
            ))}
          </ul>

          {longNames.length > 0 && (
            <p className="opt__hint">
              {longNames.length} names have three or more words, so first and last may be split wrong
              ({longNames.map((s) => `${s.first} ${s.last}`).join(', ')}). Fix any after uploading by clicking the name.
            </p>
          )}

          {result && (
            <div className={`notice ${result.refused ? 'notice--warning' : 'notice--info'}`} role="status" style={{ marginTop: 'var(--s-3)' }}>
              <span aria-hidden="true">{result.refused ? '⚠' : 'i'}</span>
              <div>
                <strong>{result.dryRun ? (result.refused ? 'This would not be uploaded.' : 'Preview: nothing has changed yet.') : 'Student list updated.'}</strong>{' '}
                {result.refused ?? `${result.added} added, ${result.updated} updated, ${result.deactivated} turned off, ${result.unchanged} unchanged.`}
                {result.noSeminar > 0 && <> {result.noSeminar} have no seminar; set one by clicking their name if they need one.</>}
                {result.skipped.length > 0 && (
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {result.skipped.slice(0, 20).map((s) => <li key={s.row}>{s.email || `Row ${s.row}`}: {s.reason}</li>)}
                    {result.skipped.length > 20 && <li>…and {result.skipped.length - 20} more</li>}
                  </ul>
                )}
              </div>
            </div>
          )}
          {result?.refused && /turn off/.test(result.refused) && (
            <label className="pick" style={{ marginTop: 'var(--s-2)' }}>
              <input type="checkbox" checked={large} onChange={(e) => setLarge(e.target.checked)} />
              <span className="pick__label">Yes, this really is the whole student body: turn those students off</span>
            </label>
          )}

          <div className="opt__bar">
            <button className="btn btn--ghost" disabled={busy || preview || unmatched.length > 0} onClick={() => send(true)}>
              {busy ? 'Checking…' : 'Preview the changes'}
            </button>
            <button className="btn" disabled={busy || preview || !result?.dryRun || (!!result.refused && !large) || unmatched.length > 0}
              onClick={() => send(false)}>
              {busy ? 'Updating…' : 'Update the student list'}
            </button>
          </div>
          {unmatched.length > 0 && <p className="opt__hint">Choose staff for: {unmatched.join(', ')}.</p>}
        </>
      )}
    </section>
  )
}
